/**
 * BLI permissions loaded from API (modules + assignments) and cached in sessionStorage.
 */

import {
  LuHome,
  LuPackage,
  LuBoxes,
  LuAlertCircle,
  LuPieChart,
  LuUsers,
  LuBuilding2,
  LuCreditCard,
  LuHelpCircle,
  LuSettings,
  LuLayoutGrid,
  LuChevronDown,
} from "react-icons/lu";
import { translate } from "../utils/Common";

export const BLI_PERMISSIONS_STORAGE_KEY = "bliPermissions";

/**
 * Settings screens. The BLI catalog carries a single Settings row, but the module has more than
 * one screen, so the sidebar splits it locally: every screen resolves to the same catalog module
 * (prefix match in `resolveModuleIdForPath`) and therefore shares its action assignments.
 */
export const SETTINGS_PERMISSIONS_PATH = "/dashboard/settings";
export const SETTINGS_EXCEPTIONS_PATH = "/dashboard/settings/exceptions";
const SETTINGS_ROOT_PATHS = ["/dashboard/settings", "/dashboard/configuracion"];

/** Dispatched after BLI payload is written to sessionStorage (login / checkToken). */
export const BLI_PERMISSIONS_UPDATED_EVENT = "boxit-bli-permissions-updated";

export function normalizePathForBli(path) {
  if (!path || typeof path !== "string") return "";
  let s = path.trim();
  if (s.length > 1 && s.endsWith("/")) s = s.slice(0, -1);
  return s;
}

export function parseBliPermissionsFromSession() {
  try {
    const raw = sessionStorage.getItem(BLI_PERMISSIONS_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== "object") return null;
    if (!Array.isArray(data.modules) || !Array.isArray(data.assignments)) return null;
    return data;
  } catch {
    return null;
  }
}

function moduleIdFromRow(m) {
  return m.moduleId ?? m.ModuleId;
}

function pathFromRow(m) {
  return m.path ?? m.Path ?? m.Value ?? "";
}

function parentIdFromRow(m) {
  return m.parentModuleId ?? m.ParentModuleId ?? null;
}

/**
 * Resolve catalog module id for current route (exact path, then longest path prefix).
 * Comparación case-insensitive para alinear con URLs reales (p. ej. GenerateQR vs generateqr).
 */
export function resolveModuleIdForPath(modules, rawPath) {
  if (!modules?.length || !rawPath) return null;
  const tryPath = (p) => {
    const n = normalizePathForBli(p.split("?")[0]);
    const nLower = n.toLowerCase();
    let bestId = null;
    let bestLen = -1;
    for (const m of modules) {
      const mp = normalizePathForBli(pathFromRow(m).split("?")[0]);
      if (!mp) continue;
      const mpLower = mp.toLowerCase();
      if (nLower === mpLower) return moduleIdFromRow(m);
      if (nLower.startsWith(`${mpLower}/`) && mpLower.length > bestLen) {
        bestLen = mpLower.length;
        bestId = moduleIdFromRow(m);
      }
    }
    return bestId;
  };
  let id = tryPath(rawPath);
  if (id != null) return id;
  const full = normalizePathForBli(rawPath);
  const fullLower = full.toLowerCase();
  for (const m of modules) {
    if (normalizePathForBli(pathFromRow(m)).toLowerCase() === fullLower)
      return moduleIdFromRow(m);
  }
  return null;
}

function assignmentMatchesModuleId(a, moduleId) {
  const aMid = a.moduleId ?? a.ModuleId;
  if (aMid == null || moduleId == null) return false;
  return aMid === moduleId || String(aMid) === String(moduleId);
}

export function getActionCodesForModuleFromAssignments(assignments, moduleId) {
  if (moduleId == null || !assignments?.length) return [];
  const codes = assignments
    .filter((a) => assignmentMatchesModuleId(a, moduleId))
    .map((a) => a.actionCode ?? a.ActionCode)
    .filter(Boolean);
  return [...new Set(codes)];
}

function expandModuleIdsWithAncestors(modules, assignmentModuleIds) {
  const byId = new Map();
  for (const m of modules) {
    byId.set(moduleIdFromRow(m), m);
  }
  const out = new Set(assignmentModuleIds);
  for (const id of assignmentModuleIds) {
    let cur = byId.get(id);
    let guard = 0;
    while (cur && parentIdFromRow(cur) != null && guard < 32) {
      const pid = parentIdFromRow(cur);
      out.add(pid);
      cur = byId.get(pid);
      guard += 1;
    }
  }
  return out;
}

/**
 * Paths (normalized) that should appear in the sidebar when BLI data drives the menu.
 * Sidebar visibility is based on catalog modules from DB (not on action assignments).
 */
export function buildAllowedSidebarPaths(modules) {
  const paths = new Set();
  if (!modules?.length) return paths;

  const moduleIds = [
    ...new Set(modules.map((m) => moduleIdFromRow(m)).filter((x) => x != null)),
  ];
  const expanded = expandModuleIdsWithAncestors(modules, moduleIds);

  for (const id of expanded) {
    const m = modules.find((row) => moduleIdFromRow(row) === id);
    const p = pathFromRow(m);
    if (p) {
      const norm = normalizePathForBli(p);
      paths.add(norm);
      if (norm === "/dashboard/configuracion" || norm === "/dashboard/settings") {
        paths.add("/dashboard/configuracion");
        paths.add("/dashboard/settings");
        paths.add(SETTINGS_EXCEPTIONS_PATH);
      }
    }
  }

  paths.add(normalizePathForBli("/dashboard/"));
  paths.add(normalizePathForBli("/dashboard"));
  return paths;
}

/**
 * Filter static role menu tree: keep nodes whose path is allowed or have allowed descendants.
 */
export function filterSidebarOptionsByBli(options, parsed) {
  if (!options?.length || !parsed?.modules || !parsed?.assignments) return options || [];
  const allowedPaths = buildAllowedSidebarPaths(parsed.modules);

  const filterList = (list) => {
    const out = [];
    for (const opt of list) {
      if (opt.subNav && opt.subNav.length > 0) {
        const sub = filterList(opt.subNav);
        if (sub.length > 0) out.push({ ...opt, subNav: sub });
        continue;
      }
      if (opt.path) {
        const p = normalizePathForBli(opt.path);
        if (allowedPaths.has(p)) out.push(opt);
      }
    }
    return out;
  };

  return filterList(options);
}

function normalizeParentId(m) {
  const p = parentIdFromRow(m);
  if (p == null || p === "") return null;
  const s = String(p);
  if (s === "0") return null;
  return s;
}

function routePathFromRaw(raw) {
  if (!raw || typeof raw !== "string") return "";
  const s = raw.trim();
  if (!s) return "";
  return s.startsWith("/") ? s : `/${s}`;
}

/** Normalized path for lookup (no querystring; trailing slash stripped except root). */
function normalizedRouteKey(pathRaw) {
  if (!pathRaw || typeof pathRaw !== "string") return "";
  const noQuery = pathRaw.split("?")[0].trim();
  return normalizePathForBli(noQuery.startsWith("/") ? noQuery : `/${noQuery}`);
}

/**
 * IdObjeto 70 — map Value → i18n key (sidebar / global). Fallback: DB DescDetalle.
 * /dashboard/incidents shared by parent + list: disambiguate with fallback title.
 */
/** Keys are normalized paths lowercased (see normalizedRouteKey). */
const BLI_PATH_TRANSLATION_KEYS = {
  "/dashboard": "sidebar.options.start",
  "/dashboard/packages": "sidebar.options.packages",
  "/dashboard/packages/load": "sidebar.options.packages.load",
  "/dashboard/packages/loadpackage": "sidebar.options.packages.loadpackage",
  "/dashboard/packages/tracking": "sidebar.options.packages.tracking",
  "/dashboard/lockers/reservations": "sidebar.options.packages.reservations",
  "/dashboard/lockers/awaitingdelivery": "sidebar.options.packages.deliveries",
  "/dashboard/billing": "global.InvoicingAndUsage",
  "/dashboard/insights": "sidebar.options.statistics",
  "/dashboard/clients": "global.clients",
  "/dashboard/companies": "sidebar.options.companies",
  "/dashboard/platforms": "sidebar.options.platforms",
  "/dashboard/platforms/general": "sidebar.options.platforms.general",
  "/dashboard/platforms/support": "sidebar.options.platforms.support",
  "/dashboard/platforms/locate": "global.locate",
  "/dashboard/platforms/generateqr": "sidebar.options.platforms.GenerateQR",
  "/dashboard/incidents/register": "global.registerIncident",
  "/dashboard/faqs": "sidebar.options.faqs",
  "/dashboard/settings": "sidebar.options.configuration",
  "/dashboard/configuracion": "sidebar.options.configuration",
  "/dashboard/settings/exceptions": "sidebar.options.configuration.exceptions",
};

function sidebarTitleFromPath(pathRaw, fallbackTitle = "") {
  const n = normalizedRouteKey(pathRaw);
  const ft = String(fallbackTitle || "").trim();
  if (!n) return ft || "";

  const lower = n.toLowerCase();

  if (lower === "/dashboard/incidents") {
    if (/list/i.test(ft)) {
      const t = translate("global.listIncident");
      if (t) return t;
    }
    const t = translate("sidebar.options.incidents");
    if (t) return t;
    return ft || pathRaw;
  }

  const keyFromMap = BLI_PATH_TRANSLATION_KEYS[lower];
  if (keyFromMap) {
    const t = translate(keyFromMap);
    if (t) return t;
  }
  return ft || pathRaw;
}

/**
 * One icon family, Lucide — the same one shadcn draws from.
 *
 * The sidebar used to mix five: solid Fa, outlined Bs, Gi, Hi and Lu, with
 * different stroke weights and optical grids on adjacent rows. `GiPlatform`, a
 * rhomboid lattice, was doing duty for "lockers".
 */
function sidebarIconForPath(pathRaw) {
  const p = (pathRaw || "").toLowerCase();
  if (p.includes("/incidents")) return <LuAlertCircle />;
  /* New / Reservations (paquetes bajo ruta lockers) — mismo icono que Packages */
  if (
    p.includes("/lockers/reservations") ||
    p.includes("/lockers/awaitingdelivery")
  ) {
    return <LuPackage />;
  }
  if (p.includes("/platforms") || p.includes("/lockers")) return <LuBoxes />;
  if (p.includes("/packages")) return <LuPackage />;
  if (p.includes("/clients")) return <LuUsers />;
  if (p.includes("/companies")) return <LuBuilding2 />;
  if (p.includes("/billing") || p.includes("/adminbilling") || p.includes("/accounting"))
    return <LuCreditCard />;
  if (p.includes("/insights")) return <LuPieChart />;
  if (p.includes("/faqs")) return <LuHelpCircle />;
  if (p.includes("/settings") || p.includes("/configuracion")) return <LuSettings />;
  if (p === "/dashboard" || p === "/dashboard/" || p.endsWith("/dashboard") || p === "" )
    return <LuHome />;
  return <LuLayoutGrid />;
}

// One chevron that rotates, instead of two solid triangles swapping. The user
// menu already used a stroked chevron, so the same gesture had two grammars.
const submenuArrowIcons = {
  iconClosed: <LuChevronDown />,
  iconOpened: <LuChevronDown />,
};

function isSettingsRootPath(pathRaw) {
  return SETTINGS_ROOT_PATHS.includes(normalizedRouteKey(pathRaw).toLowerCase());
}

/**
 * Screens of the Settings module, appended to whatever the catalog already declares as children
 * (matched by path so a real catalog row always wins over the local entry).
 */
function withSettingsScreens(subNav = []) {
  const screens = [
    { path: SETTINGS_PERMISSIONS_PATH, titleKey: "sidebar.options.configuration.permissions", fallback: "Permissions" },
    { path: SETTINGS_EXCEPTIONS_PATH, titleKey: "sidebar.options.configuration.exceptions", fallback: "Exceptions" },
  ].map(({ path, titleKey, fallback }) => ({ title: translate(titleKey) || fallback, path }));

  const seen = new Set(subNav.map((s) => normalizedRouteKey(s.path).toLowerCase()));

  return [...subNav, ...screens.filter((s) => !seen.has(normalizedRouteKey(s.path).toLowerCase()))];
}

/**
 * Sidebar from BLI catalog only: all rows in `modules` (ParentModuleId defines the tree).
 * Assignments are not used for visibility — same list for everyone with a BLI payload.
 */
export function buildBliSidebarOptionsFromCatalog(parsed) {
  const modules = parsed?.modules;
  if (!Array.isArray(modules) || modules.length === 0) return [];

  const byId = new Map();
  for (const m of modules) {
    const idRaw = moduleIdFromRow(m);
    if (idRaw == null || idRaw === "") continue;
    const id = String(idRaw);
    const pathVal = pathFromRow(m);
    byId.set(id, {
      id,
      parentId: normalizeParentId(m),
      title: String(m.moduleName ?? m.ModuleName ?? m.DescDetalle ?? "").trim() || id,
      pathRaw: pathVal,
      orden: (m.orden ?? m.Orden ?? parseInt(id, 10)) || 0,
    });
  }

  const visible = [...byId.values()];
  const visibleIds = new Set(visible.map((v) => v.id));

  const childrenByParent = new Map();
  for (const n of visible) {
    const pkey = n.parentId;
    if (!childrenByParent.has(pkey)) childrenByParent.set(pkey, []);
    childrenByParent.get(pkey).push(n);
  }
  for (const [, arr] of childrenByParent) {
    arr.sort((a, b) => a.orden - b.orden || a.id.localeCompare(b.id));
  }

  function descendantLeafLinks(nodeId) {
    const kids = childrenByParent.get(nodeId) || [];
    if (kids.length === 0) {
      const n = byId.get(nodeId);
      if (!n) return [];
      const rp = routePathFromRaw(n.pathRaw);
      if (!rp) return [];
      return [{ title: sidebarTitleFromPath(rp, n.title), path: rp }];
    }
    const acc = [];
    for (const k of kids) acc.push(...descendantLeafLinks(k.id));
    return acc;
  }

  const roots = visible.filter((n) => !n.parentId || !visibleIds.has(n.parentId));
  roots.sort((a, b) => a.orden - b.orden || a.id.localeCompare(b.id));

  const options = [];
  for (const r of roots) {
    const kids = childrenByParent.get(r.id) || [];
    if (kids.length === 0) {
      const rp = routePathFromRaw(r.pathRaw);
      if (!rp) continue;
      options.push({
        title: sidebarTitleFromPath(rp, r.title),
        path: rp,
        icon: sidebarIconForPath(rp),
        ...submenuArrowIcons,
        // Settings has no catalog children but does have several screens.
        ...(isSettingsRootPath(rp) ? { subNav: withSettingsScreens() } : {}),
      });
      continue;
    }
    let subNav = [];
    for (const k of kids) subNav.push(...descendantLeafLinks(k.id));
    if (isSettingsRootPath(r.pathRaw)) subNav = withSettingsScreens(subNav);
    if (subNav.length === 0) continue;

    const parentRoute = routePathFromRaw(r.pathRaw);
    const iconPath = parentRoute || (subNav[0] && subNav[0].path) || "";
    options.push({
      title: sidebarTitleFromPath(parentRoute || subNav[0]?.path || "", r.title),
      path: parentRoute || subNav[0].path,
      icon: sidebarIconForPath(iconPath),
      ...submenuArrowIcons,
      subNav,
    });
  }

  return options;
}

export function notifyBliPermissionsUpdated() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(BLI_PERMISSIONS_UPDATED_EVENT));
  }
}
